import { useState } from 'react';
import { useTheme } from 'styled-components';
import useGetDevices from '../../API/hooks/devices/useGetDevices';
import useGetDevicesLocations from '../../API/hooks/maps/useGetDevicesLocations';
import { useGetDeviceLocationsByIds } from '../../API/hooks/maps/useGetDeviceLocations';
import DoghnutChartWidget from '../../components/common/DoghnutChartWidget/DoghnutChartWidget';
import { FilterConfig } from '../../components/common/DropdownFilter/types';
import ItemsList from '../../components/common/ItemsList/ItemsList';
import Modal from '../../components/common/Modal/Modal';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import MapWidget from '../../components/common/Map/Map';
import { ThemeType } from '../../theme/themeType';
import S from './DevicesPageStyles';
import DevicesItem from './DevicesItem/DevicesItem';
import useGetDeviceStatus from '../../API/hooks/devices/useGetDeviceStatus';
import { DeviceStatusDognutConfig, DeviceStatusKeys } from '../../API/types/Devices';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetRole from '../../API/hooks/useGetRole';
import { AddDevice } from './Modals';
import GlobalAlertsConfiguration from './Modals/GlobalAlertsConfiguration/GlobalAlertsConfiguration';
import Tabs from '../../components/common/Tabs/Tabs';
import { getLocalStorageKey } from '../../utils/storageUtils';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetDevicesReport from '../../API/hooks/reports/useGetDevicesReport';
import GroupSelection from '../../components/GroupSelection/GroupSelection';
import Icon from '../../components/common/Icon/Icon';
import { downloadFile, createCSV } from '../../utils/commonUtils';
import useGetCurrentPage from '../../API/hooks/useGetCurrentPage';
import VideoSettingsItem from '../DeviceDetails/Modals/DeviceSettings/VideoSettingsItem/VideoSettingsItem';
import { queryFilterByGroups } from '../../utils/queryFilters';
import { devicesPageFilters } from '../../constants/FilterDefault';
import useLocalStorage, { useGroupFilters } from '../../API/hooks/useLocalStorage';
import AudioSchemaSettingsItem from './Modals/AudioSchemaSettingsItem/AudioSchemaSettingsItem';
import DevicesConfiguration from './Modals/DevicesConfiguration/DevicesConfiguration';
import DevicesCalibration from './Modals/DevicesCalibration/DevicesCalibration';
import HWFleetConfiguration from './Modals/HWFleetConfiguration/HWFleetConfiguration';
import Tooltip from '../../components/common/Tooltip/Tooltip';
import useGetFleetConfigStatus from '../../API/hooks/devices/useGetFleetConfigStatus';
import useOngoingTripMapIds from '../../API/hooks/trips/useOngoingTripMapIds';

const SEARCH_CONFIG = [
  {
    type: 'isOnline',
    display: 'radio',
    options: [
      { null: 'All' },
      { true: 'Online' },
      { false: 'Offline' },
    ],
  },
  {
    type: 'vehicle._id',
    display: 'radio',
    options: [
      { null: 'All' },
      { true: 'Assigned' },
      { false: 'Unassigned' },
    ],
  },
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { isOnline_ASC: 'Is online: asc' },
      { isOnline_DESC: 'Is online: desc' },
      { activeAt_ASC: 'Last active: asc' },
      { activeAt_DESC: 'Last active: desc' },
      { deviceId_ASC: 'Device Id: asc' },
      { deviceId_DESC: 'Device Id: desc' },
    ],
  },
] as FilterConfig[];

function DevicesPage(): JSX.Element {
  const theme = useTheme() as ThemeType;

  const deviceChartConfig: DeviceStatusDognutConfig = {
    cards: {
      online: {
        color: theme.colors.green,
        label: 'Online',
      },
      offline: {
        color: theme.colors.main_cta,
        label: 'Offline',
      },
      unresponsive: {
        color: theme.colors.red,
        label: 'Not resp.',
      },
    },
    title: 'Devices status',
  };

  const deviceAssignmentChartConfig: DeviceStatusDognutConfig = {
    cards: {
      assigned: {
        color: theme.colors.main_cta,
        label: 'Assigned',
      },
      unassigned: {
        color: theme.colors.red,
        label: 'Not assigned',
      },
    },
    title: 'Devices assigned',
  };

  const fleetId = useGetFleetId();
  const { isFleetViewer } = useGetRole();
  const [groupFilters, setGroupFilters] = useGroupFilters();
  const { data: deviceChartData, isLoading } = useGetDeviceStatus(fleetId, `?${queryFilterByGroups(groupFilters || {}).slice(1)}`);
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('devices-page-filters'), devicesPageFilters);
  const { data: fleetData } = useGetFleet(fleetId);
  const [addDeviceModalOpen, toggleAddDeviceModal] = useState(false);

  const [deviceSettingModalOpen, toggleChangeSettingsModal] = useState(false);

  const { data: fleetConfigStatusResponse } = useGetFleetConfigStatus(fleetId);
  const fleetConfigStatus = fleetConfigStatusResponse?.fleetConfigStatus;
  const hasValidFleetConfigStatus = fleetConfigStatus === 'success' || fleetConfigStatus === 'pending';

  const BUTTONS = [
    {
      title: 'Global Settings',
      iconKey: IconKeys.SETTINGS,
      action: () => toggleChangeSettingsModal(true),
      hide: isFleetViewer,
      padding: hasValidFleetConfigStatus ? '6px 30px 6px 16px' : undefined,
    },
  ];

  Object.keys(deviceChartConfig.cards).forEach((key): void => {
    deviceChartConfig.cards[key as DeviceStatusKeys]!.count = deviceChartData?.[key as DeviceStatusKeys] || 0;
  });

  Object.keys(deviceAssignmentChartConfig.cards).forEach((key): void => {
    deviceAssignmentChartConfig.cards[key as DeviceStatusKeys]!.count = deviceChartData?.[key as DeviceStatusKeys] || 0;
  });

  const alertsContent = (): any => (
    <GlobalAlertsConfiguration
      fleetId={fleetId}
      toggleModal={toggleChangeSettingsModal}
    />
  );

  const configurationEventsContent = (): any => (
    <DevicesConfiguration toggleModal={toggleChangeSettingsModal} />
  );

  const configurationCalibrationContent = (): any => (
    <DevicesCalibration toggleModal={toggleChangeSettingsModal} />
  );

  const hwConfigurationContent = (): any => (
    <HWFleetConfiguration toggleModal={toggleChangeSettingsModal} />
  );

  const liveVideoContent = (): any => (
    <VideoSettingsItem fleetId={fleetId} global toggleModal={toggleChangeSettingsModal} />
  );

  const audioSchemaContent = (): any => (
    <AudioSchemaSettingsItem toggleModal={toggleChangeSettingsModal} />
  );

  const tabsContent = [
    {
      label: 'Alerts',
      id: 'alerts-devices-settings',
      content: <GlobalAlertsConfiguration
        fleetId={fleetId}
        toggleModal={toggleChangeSettingsModal}
      />,
    },
    // {
    //   label: 'HW Configuration (Events)',
    //   id: 'device-configuration-events',
    //   content: configurationEventsContent,
    // },
    // {
    //   label: 'HW Configuration (Calibration)',
    //   id: 'device-configuration-calibration',
    //   content: configurationCalibrationContent,
    // },
    {
      label: 'HW Configuration',
      id: 'hw-configuration',
      content: <HWFleetConfiguration toggleModal={toggleChangeSettingsModal} />,
    },
    {
      label: 'Live Video',
      id: 'live-video-devices-settings',
      content: <VideoSettingsItem fleetId={fleetId} global toggleModal={toggleChangeSettingsModal} />,
    },
    {
      label: 'Audio Schema',
      id: 'audio-schema-devices-settings',
      content: <AudioSchemaSettingsItem toggleModal={toggleChangeSettingsModal} />,
    },
  ];

  const reportName = `${fleetData?.name || 'fleet_na'}_devices`;
  const { ongoingDeviceIds, ongoingTripSpeedDisplayByDeviceId } = useOngoingTripMapIds(fleetId);

  return (
    <>
      <S.Wrapper>
        <div className="fleet-config-main">
          <Toolbar title="Devices" buttons={BUTTONS} />
          {!isFleetViewer && fleetConfigStatus && (
            <div className="fleet-config-status">
              <Tooltip
                content={
                  fleetConfigStatus === 'success'
                    ? 'Success'
                    : 'Pending'
                }
              >
                <span className="fleet-config-icon">
                  <Icon iconKey={fleetConfigStatus === 'success' ? IconKeys.SYNCED_GLOBAL : IconKeys.SYNC_PENDING_GLOBAL} />
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="sub-header">
          <GroupSelection filters={groupFilters || {}} setFilters={setGroupFilters} />
        </div>
        <div className="widgets-container">
          <MapWidget
            fleetId={fleetId}
            entityType="device"
            getLocationsQuery={useGetDevicesLocations}
            getLocationDetailsQuery={useGetDeviceLocationsByIds}
            groupFilters={groupFilters || {}}
            fleetOngoingDeviceIds={ongoingDeviceIds}
            ongoingTripSpeedDisplayByDeviceId={ongoingTripSpeedDisplayByDeviceId}
            showSpeedInVehicleInfo
          />
          <div className="statistics-container">
            <button
              className="report-btn"
              title="Create report"
              type="button"
              onClick={() => {
                downloadFile(createCSV([{
                  ...deviceChartData,
                }]), `${reportName}_widgets`, 'text/csv');
              }}
            >
              <Icon iconKey={IconKeys.SUMMARIZE} />
            </button>
            <DoghnutChartWidget data={deviceChartConfig} loading={isLoading} />
            <DoghnutChartWidget data={deviceAssignmentChartConfig} type="double" loading={isLoading} />
          </div>
        </div>
        <ItemsList
          entity="devices"
          fleetId={fleetId}
          filtersConfig={SEARCH_CONFIG}
          filters={filters}
          defaultFilters={devicesPageFilters}
          setFilters={setFilters}
          groupFilters={groupFilters || {}}
          getQuery={useGetDevices}
          hasSearch
          hasCreateReport
          reportEndpoint={useGetDevicesReport}
          reportName={reportName}
          curentPage={useGetCurrentPage()}
        >
          <DevicesItem />
        </ItemsList>
      </S.Wrapper>
      {addDeviceModalOpen && (
        <Modal
          title="Add new device"
          state={addDeviceModalOpen}
          toggleState={toggleAddDeviceModal}
        >
          <AddDevice fleetId={fleetId} toggleModal={toggleAddDeviceModal} />
        </Modal>
      )}
      {deviceSettingModalOpen && (
        <Modal
          title="Global settings for all devices in fleet"
          state={deviceSettingModalOpen}
          toggleState={toggleChangeSettingsModal}
          settings={{ maxWidth: '600px', minHeight: '400px', display: 'block' }}
        >
          <Tabs
            tabs={tabsContent}
          />
        </Modal>
      )}
    </>
  );
}

export default DevicesPage;
